import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';
import {
  LayoutDashboard,
  Users,
  Building2,
  Home,
  Target,
  Calendar,
  FileText,
  TrendingUp,
  DollarSign,
  Clock,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  Plus,
  Loader2,
  Activity,
  BarChart3,
  Briefcase,
  Phone,
  Mail,
  Sparkles,
  Zap,
  Crown,
  Handshake,
  Globe,
  Bot,
  CreditCard,
  Bell,
  Share2
} from 'lucide-react';

const CRMDashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [pipeline, setPipeline] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchDashboardData();
  }, [isAuthenticated]);

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, activitiesRes, pipelineRes] = await Promise.all([
        api.get('/crm/dashboard/summary'),
        api.get('/crm/activities?limit=5'),
        api.get('/crm/opportunities/pipeline')
      ]);
      
      setSummary(summaryRes.data);
      setRecentActivities(activitiesRes.data.activities || []);
      setPipeline(pipelineRes.data);
    } catch (error) {
      console.error('Error fetching CRM dashboard:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el dashboard del CRM",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  const kpis = summary?.kpis || {};

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <LayoutDashboard className="h-8 w-8" />
                  <h1 className="text-2xl lg:text-3xl font-bold">CRM UPIM</h1>
                </div>
                <p className="text-gray-300">
                  Gestiona tus contactos, propiedades y oportunidades
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button 
                  className="bg-white text-gray-900 hover:bg-gray-100"
                  onClick={() => navigate('/crm/contacts/new')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Contacto
                </Button>
                <Button 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => navigate('/crm/properties/new')}
                >
                  <Home className="h-4 w-4 mr-2" />
                  Nueva Propiedad
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* UPIM IA Promo Banner */}
          <Card className="mb-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0 overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="h-14 w-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-7 w-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold flex items-center gap-2">
                      UPIM IA – Altitud Inmobiliaria
                      <Badge className="bg-yellow-500 text-yellow-900">Premium</Badge>
                    </h3>
                    <p className="text-blue-100 mt-1">
                      Potencia tu CRM con Inteligencia Artificial equivalente a Salesforce Einstein
                    </p>
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-blue-100">
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="h-4 w-4" />
                        Predicción de cierre
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="h-4 w-4" />
                        Asesoría legal MX
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="h-4 w-4" />
                        Omnicanal con IA
                      </span>
                    </div>
                  </div>
                </div>
                <Button 
                  className="bg-white text-blue-600 hover:bg-blue-50 flex-shrink-0"
                  onClick={() => navigate('/upim-ia')}
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Activar por $1,000/mes
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/crm/contacts')}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Contactos</p>
                    <p className="text-3xl font-bold text-gray-900">{kpis.contacts || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/crm/properties')}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Propiedades</p>
                    <p className="text-3xl font-bold text-gray-900">{kpis.properties || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-green-100 flex items-center justify-center">
                    <Home className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/crm/pipeline')}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Oportunidades</p>
                    <p className="text-3xl font-bold text-gray-900">{kpis.open_opportunities || 0}</p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Target className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Pipeline</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(kpis.pipeline_value)}</p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Pipeline Mini View */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Pipeline de Ventas
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={() => navigate('/crm/pipeline')}>
                      Ver completo
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {pipeline && Object.keys(pipeline.pipeline || {}).length > 0 ? (
                    <div className="space-y-4">
                      {Object.entries(pipeline.pipeline || {}).slice(0, 5).map(([stage, data]) => (
                        <div key={stage} className="flex items-center gap-4">
                          <div className="w-32 text-sm text-gray-600 truncate">{data.name}</div>
                          <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden relative">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500"
                              style={{ 
                                width: `${Math.min((data.count / Math.max(pipeline.summary?.total_opportunities || 1, 1)) * 100, 100)}%` 
                              }}
                            />
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                              {data.count} - {formatCurrency(data.total_amount)}
                            </span>
                          </div>
                          <div className="w-12 text-right text-sm text-gray-500">
                            {data.probability}%
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No hay oportunidades en el pipeline</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3"
                        onClick={() => navigate('/crm/opportunities/new')}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Crear primera oportunidad
                      </Button>
                    </div>
                  )}

                  {pipeline?.summary && (
                    <div className="mt-6 pt-4 border-t flex items-center justify-between text-sm">
                      <div>
                        <span className="text-gray-500">Valor ponderado: </span>
                        <span className="font-bold text-gray-900">{formatCurrency(pipeline.summary.weighted_value)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Ganado este mes: </span>
                        <span className="font-bold text-green-600">{formatCurrency(kpis.won_this_month)}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                <Button 
                  variant="outline" 
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => navigate('/crm/contacts')}
                >
                  <Users className="h-6 w-6 text-blue-600" />
                  <span>Contactos</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => navigate('/crm/properties')}
                >
                  <Home className="h-6 w-6 text-green-600" />
                  <span>Propiedades</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => navigate('/crm/accounts')}
                >
                  <Building2 className="h-6 w-6 text-purple-600" />
                  <span>Cuentas</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => navigate('/crm/activities')}
                >
                  <Calendar className="h-6 w-6 text-amber-600" />
                  <span>Actividades</span>
                </Button>
              </div>

              {/* CRM Tools Section - NEW */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-indigo-600" />
                    Herramientas CRM
                  </CardTitle>
                  <CardDescription>
                    Potencia tu negocio con estas herramientas exclusivas UPIM
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-indigo-50 hover:border-indigo-300"
                      onClick={() => navigate('/crm/propiedades')}
                    >
                      <Home className="h-6 w-6 text-indigo-600" />
                      <span className="text-xs">Mis Propiedades</span>
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-purple-50 hover:border-purple-300"
                      onClick={() => navigate('/crm/alianzas')}
                    >
                      <Handshake className="h-6 w-6 text-purple-600" />
                      <span className="text-xs">Alianzas</span>
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-300"
                      onClick={() => navigate('/crm/micrositio')}
                    >
                      <Globe className="h-6 w-6 text-blue-600" />
                      <span className="text-xs">Mi Micrositio</span>
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-green-50 hover:border-green-300"
                      onClick={() => navigate('/crm/bot-clientes')}
                    >
                      <Bot className="h-6 w-6 text-green-600" />
                      <span className="text-xs">Bot IA Clientes</span>
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-amber-50 hover:border-amber-300"
                      onClick={() => navigate('/crm/credencial-digital')}
                    >
                      <CreditCard className="h-6 w-6 text-amber-600" />
                      <span className="text-xs">Mi Credencial</span>
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-rose-50 hover:border-rose-300"
                      onClick={() => navigate('/crm/reportes-mercado')}
                    >
                      <TrendingUp className="h-6 w-6 text-rose-600" />
                      <span className="text-xs">Reportes IA</span>
                    </Button>
                  </div>
                  
                  {/* Secondary tools */}
                  <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-gray-600"
                      onClick={() => navigate('/crm/alertas')}
                    >
                      <Bell className="h-4 w-4 mr-2" />
                      Alertas de Aliados
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Alerts */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                    Alertas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {kpis.overdue_activities > 0 && (
                    <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                      <Clock className="h-5 w-5 text-red-500" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-red-800">
                          {kpis.overdue_activities} actividades vencidas
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => navigate('/crm/activities?overdue=true')}>
                        Ver
                      </Button>
                    </div>
                  )}
                  
                  {kpis.today_activities > 0 && (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                      <Calendar className="h-5 w-5 text-blue-500" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-800">
                          {kpis.today_activities} actividades para hoy
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => navigate('/crm/activities?due_today=true')}>
                        Ver
                      </Button>
                    </div>
                  )}

                  {kpis.overdue_activities === 0 && kpis.today_activities === 0 && (
                    <div className="text-center py-4 text-gray-500">
                      <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                      <p className="text-sm">Todo al día</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Activities */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Actividad Reciente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recentActivities.length > 0 ? (
                    <div className="space-y-3">
                      {recentActivities.map((activity) => (
                        <div key={activity.id} className="flex items-start gap-3">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            activity.is_completed ? 'bg-green-100' : 'bg-gray-100'
                          }`}>
                            {activity.activity_type === 'llamada' && <Phone className="h-4 w-4 text-gray-600" />}
                            {activity.activity_type === 'email' && <Mail className="h-4 w-4 text-gray-600" />}
                            {activity.activity_type === 'visita' && <Home className="h-4 w-4 text-gray-600" />}
                            {activity.activity_type === 'tarea' && <CheckCircle2 className="h-4 w-4 text-gray-600" />}
                            {!['llamada', 'email', 'visita', 'tarea'].includes(activity.activity_type) && 
                              <Activity className="h-4 w-4 text-gray-600" />
                            }
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{activity.subject}</p>
                            <p className="text-xs text-gray-500">
                              {activity.due_date ? new Date(activity.due_date).toLocaleDateString('es-MX') : 'Sin fecha'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Sin actividades recientes</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CRMDashboard;
